import { createContext, useContext } from 'react'

import { useMutation } from '@api/hooks'
import { MarketBasketWithSettings } from '@domain'
import MarketBasketsApi from '@services/marketBaskets.api'
import { useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'

export function useAddMarketBasket() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: MarketBasketsApi.add,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market-baskets'] }),
		onError: (error: AxiosError<{ message: string }>) => {
			// Override global error handling to not display toast if focusing field
			const errorMessage = error?.response?.data?.message
			if (
				// Cases where we focus and error a field
				!errorMessage.startsWith('A Market Basket with the name') &&
				!errorMessage.startsWith('A Market Basket with Salesforce')
			) {
				toast.error(errorMessage, { duration: 20000 })
			}
		},
	})
}

export function useUpdateMarketBasket(marketBasketId: string) {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: MarketBasketsApi.update,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['market-baskets'] })
			queryClient.invalidateQueries({ queryKey: ['market-basket', marketBasketId] })
		},
		onError: (error: AxiosError<{ message: string }>) => {
			// Override global error handling to not display toast if focusing field
			const errorMessage = error?.response?.data?.message
			if (
				// Cases where we focus and error a field
				!errorMessage.startsWith('A Market Basket with the name') &&
				!errorMessage.startsWith('A Market Basket with Salesforce')
			) {
				toast.error(errorMessage, { duration: 20000 })
			}
		},
	})
}

// Context and hooks for a Market Basket
export type MarketBasketContextType = {
	marketBasket: MarketBasketWithSettings
	isLoading: boolean
	isRefreshing: boolean
	isAnalysisComplete: boolean
	isPalantirBasket: boolean
}

export const MarketBasketContext = createContext<MarketBasketContextType | undefined>(undefined)

export function useMarketBasketContext() {
	const context = useContext(MarketBasketContext)
	if (!context) throw new Error('useMarketBasketContext must be used within a MarketBasketProvider')
	return context
}
